<main class="canvas-wide">
  <a routerLink="/releases" class="back-link" style="margin-bottom: 12px; display: inline-flex">
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      width="14"
      height="14"
    >
      <path d="m15 18-6-6 6-6" />
    </svg>
    Releases
  </a>

  <div class="admin-head">
    <div>
      <div class="eyebrow">Admin</div>
      <h1>Administration <span class="badge">Admin only</span></h1>
      <p style="color: var(--text-soft); margin-top: 6px">
        Manage who has access and configure releases.
      </p>
    </div>
  </div>

  <!-- Tabs -->
  <div class="tabs">
    <a class="tab" routerLink="/admin">Users &amp; access</a>
    <a class="tab active" routerLink="/admin/releases">Manage releases</a>
  </div>

  <!-- Existing releases -->
  <div class="panel-card">
    <div class="panel-card-head">
      <h2>Existing releases</h2>
      <button class="btn-sm primary" type="button" (click)="startCreate()" [disabled]="!!creating">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
          <path d="M12 5v14M5 12h14" />
        </svg>
        Create new release
      </button>
    </div>

    <!-- Inline create row (AC10) -->
    @if (creating) {
      <div class="release-item open create-row">
        <div class="release-summary as-create">
          <svg
            class="caret open"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="m9 18 6-6-6-6" />
          </svg>
          <input
            class="name-input"
            type="text"
            name="newReleaseName"
            placeholder="Release name…"
            [(ngModel)]="creating.name"
            autocomplete="off"
          />
          <span class="status-tag new">New</span>
          <span class="dates">Not yet saved</span>
        </div>
        <div class="release-body">
          <div class="config-fields">
            <div class="field">
              <label>Azure release id</label>
              <input
                type="number"
                name="newAzureId"
                placeholder="e.g. 12345"
                [(ngModel)]="creating.azureId"
              />
            </div>
            <div class="field">
              <label>Open date</label>
              <input type="date" name="newOpenOn" [(ngModel)]="creating.openOn" />
            </div>
            <div class="field">
              <label>Sign-off due</label>
              <input type="date" name="newSignOff" [(ngModel)]="creating.signOffDue" />
            </div>
            <div class="field"></div>
            <div class="row-actions">
              <button class="btn-sm" type="button" (click)="cancelCreate()">Cancel</button>
              <button
                class="btn-sm primary"
                type="button"
                [disabled]="!creating.name.trim() || creating.azureId == null || creating.saving"
                (click)="saveCreate()"
              >
                {{ creating.importing ? 'Importing plans…' : 'Save release' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    }

    @if (isLoading()) {
      @for (row of skeletonRows; track row) {
        <div class="release-item">
          <div class="release-summary skel-summary" aria-hidden="true">
            <span class="skel skel-caret"></span>
            <span class="skel skel-name"></span>
            <span class="skel skel-tag"></span>
            <span class="skel skel-dates"></span>
          </div>
        </div>
      }
    } @else if (releases().length === 0) {
      <div class="empty-row">No releases yet.</div>
    } @else {
      @for (r of releases(); track r.releaseId) {
        <div class="release-item" [class.open]="isExpanded(r)">
          <button
            class="release-summary"
            type="button"
            (click)="toggle(r)"
            [attr.aria-expanded]="isExpanded(r)"
          >
            <svg
              class="caret"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="m9 18 6-6-6-6" />
            </svg>
            <span class="name">{{ r.releaseName }}</span>
            <span class="status-tag {{ statusClass(r.status) }}">{{ r.status }}</span>
            <span class="dates">{{ dateRange(r) }}</span>
          </button>

          @if (isExpanded(r)) {
            <div class="release-body" [class.locked]="isLocked(r)">
              @if (isLocked(r)) {
                <div class="locked-note">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <rect x="3" y="11" width="18" height="11" rx="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                  This release is {{ r.status | lowercase }} and locked. Change status back to
                  Active to edit.
                </div>
              }

              <div class="config-fields">
                <div class="field">
                  <label>Status</label>
                  <!-- Status select stays enabled even when locked: it is the only
                       control that can re-enable a shipped/archived release. -->
                  <app-select
                    ariaLabel="Status"
                    [options]="statusOptions"
                    [(ngModel)]="edits[r.releaseId].status"
                    (ngModelChange)="onStatusChange(r)"
                  />
                </div>
                <div class="field">
                  <label>Open date</label>
                  <input
                    type="date"
                    name="openOn-{{ r.releaseId }}"
                    [(ngModel)]="edits[r.releaseId].openOn"
                    [disabled]="isLocked(r)"
                  />
                </div>
                <div class="field">
                  <label>Sign-off due</label>
                  <input
                    type="date"
                    name="signOff-{{ r.releaseId }}"
                    [(ngModel)]="edits[r.releaseId].signOffDue"
                    [disabled]="isLocked(r)"
                  />
                </div>
                <div class="field">
                  <label>Test plans</label>
                  <a class="btn-sm configure-link" [routerLink]="['/admin/releases', r.releaseId]">
                    {{ r.numberOfTestPlans }} plan{{ r.numberOfTestPlans === 1 ? '' : 's' }} ·
                    Configure
                  </a>
                </div>
                <div class="row-actions">
                  <button
                    class="btn-sm primary"
                    type="button"
                    [disabled]="isLocked(r) || isSaving(r)"
                    (click)="save(r)"
                  >
                    {{ isSaving(r) ? 'Saving…' : 'Save' }}
                  </button>
                </div>
              </div>
            </div>
          }
        </div>
      }
    }
  </div>
</main>
